import {FC, useEffect, useState, useRef} from 'react'
import {getInitials, getStyleByIndex, UrlBuilder, UrlParamReplace} from 'app/helpers/MiscUtils'
import {MenuInner} from './MenuInner'
import {toAbsoluteUrl} from '_metronic/helpers'
import {useAuth} from 'app/context/auth-context'

import {VideoSessionStatus} from 'app/API'
import {useAppContext} from 'app/context/app-context'
import {AlertModal} from 'app/modules/shared/modals/AlertModal'
import {HeaderNotificationPanel} from './HeaderNotificationPanel'
import {Roles} from 'app/types'
import moment from 'moment'
import {RxSubscription} from 'app/RxSubscriptions'
import {Link} from 'react-router-dom'
import {useS3Object} from 'app/context/S3ObjectContext'
import {InterviewSessionService} from 'app/services/InterviewSessionService'
import JobFairSelector from './JobFairSelector'
import useWebSocket from 'app/hooks/useWebsocket'
import {JobFairStatusComponent} from './JobFairStatusComponent'
import {JSRoutes} from 'app/modules/job-seeker/JSPage'
import {AdminMenuItems} from './AdminMenuItems'
import {AdminRoutes} from 'app/modules/admin/AdminPage'
import {RecruiterRoutes} from 'app/modules/recruiter/RecruiterPage'
import {EmployerAdminRoutes} from 'app/modules/employer-admin/EmployerAdminPage'

const Header: FC = () => {
  const {user} = useAuth()

  const [showDesktopOnlyAlert, setShowDesktopOnlyAlert] = useState<boolean>(false)
  const isSocketConnected = true
  useWebSocket()
  const [showOfflinePopup, setShowOfflinePopup] = useState<boolean>(false)
  const {jobFair} = useAppContext()

  // User profile menu state
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState<boolean>(false)
  const profileMenuRef = useRef<HTMLDivElement>(null)
  const {getS3ObjectUrl, s3ObjectCache} = useS3Object()
  const avatarUrl = user?.avatar ? s3ObjectCache[user.avatar] : null

  useEffect(() => {
    const isMobileOrTablet = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    )
    if (isMobileOrTablet) {
      setShowDesktopOnlyAlert(true)
    }
  }, [])

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setIsProfileMenuOpen(false)
      }
    }

    if (isProfileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isProfileMenuOpen])

  useEffect(() => {
    if (!isSocketConnected) {
      const timeOutRef = setTimeout(() => setShowOfflinePopup(true), 10000)
      return () => clearTimeout(timeOutRef)
    } else {
      setShowOfflinePopup(false)
    }
  }, [isSocketConnected])

  const [isInterviewInProgress, setIsInterviewInProgress] = useState<boolean>(false)

  useEffect(() => {
    if (!user?.roles || !jobFair?.id) return

    const isApplicant = user.roles.includes(Roles.Applicant)
    const isRecruiter = user.roles.includes(Roles.Recruiter)

    if (!isApplicant && !isRecruiter) return

    // Initial check
    const interviewSessionService = new InterviewSessionService()
    interviewSessionService
      .getActiveInterviewsByRecruiter(jobFair.id, user.id)
      .then((sessions: any) => {
        setIsInterviewInProgress(sessions && sessions.length > 0)
      })
      .catch(console.error)

    const observable = isApplicant
      ? RxSubscription.onChangeInterviewSessionByCandidateIdObservable()
      : RxSubscription.onChangeInterviewSessionByRecruiterIdObservable()

    const subscription = observable.subscribe((changeEvent) => {
      setIsInterviewInProgress(changeEvent?.value?.status === VideoSessionStatus.IN_PROGRESS)
    })

    return () => subscription.unsubscribe()
  }, [user?.roles, jobFair?.id])

  useEffect(() => {
    if (user?.avatar) {
      getS3ObjectUrl(user.avatar)
    }
  }, [user?.avatar, getS3ObjectUrl])

  const toggleProfileMenu = () => {
    if (isInterviewInProgress) return
    setIsProfileMenuOpen((prev) => !prev)
  }

  const isAdmin = user?.roles?.includes(Roles.Admin)
  const isApplicant = user?.roles?.includes(Roles.Applicant)
  const isRecruiter = user?.roles?.includes(Roles.Recruiter)
  const isEmployerAdmin = user?.roles?.includes(Roles.EmployerAdmin)

  const getProfileLink = () => {
    if (isAdmin) return AdminRoutes.Profile
    if (isRecruiter) return RecruiterRoutes.Profile
    if (isEmployerAdmin) return EmployerAdminRoutes.Profile
    if (isApplicant) return JSRoutes.Profile
    return '#'
  }

  return (
    <header>
      <div className='container-fluid'>
        <div className='flex'>
          <div className='logo'>
            <a href='#'>
              <img src={toAbsoluteUrl('/media/logos/logo.png')} alt='Video JobFair' />
            </a>
          </div>
          {!isAdmin && (isApplicant ? <JobFairStatusComponent /> : <JobFairSelector />)}
          <nav>
            {isAdmin ? (
              <ul id='menu'>
                <AdminMenuItems />
              </ul>
            ) : (
              <ul id='menu' className={isInterviewInProgress || !jobFair ? 'vjfpDisabled' : ''}>
                <MenuInner />
              </ul>
            )}
            {/* Notifications */}
            {!isAdmin && <HeaderNotificationPanel />}
            {/* User Profile */}
            <div className='userProfile' ref={profileMenuRef}>
              <a
                className='menu-dropdown'
                onClick={toggleProfileMenu}
                style={{cursor: isInterviewInProgress ? 'not-allowed' : 'pointer'}}
              >
                {avatarUrl ? (
                  <div className='symbol symbol-50px symbol-circle'>
                    <img src={avatarUrl} crossOrigin='anonymous' alt='User Avatar' />
                  </div>
                ) : (
                  <div className='symbol symbol-50px symbol-circle'>
                    <div
                      className={
                        'symbol-label fs-3 fw-bold bg-' +
                        getStyleByIndex(0) +
                        ' text-inverse-' +
                        getStyleByIndex(0)
                      }
                    >
                      {getInitials(user?.firstName, user?.lastName)}
                    </div>
                  </div>
                )}
              </a>
              <div
                className='menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-800 menu-state-bg menu-state-primary fw-bold py-4 fs-6'
                style={{
                  display: isProfileMenuOpen ? 'block' : 'none',
                  position: 'absolute',
                  right: 0,
                  top: '100%',
                  marginTop: '0.5rem',
                  minWidth: '300px',
                  maxWidth: '400px',
                  width: 'auto',
                }}
              >
                <div className='menu-item px-3'>
                  <div className='menu-content d-flex align-items-center px-3'>
                    <div className='symbol symbol-50px me-5'>
                      {avatarUrl ? (
                        <div className='symbol symbol-circle'>
                          <img src={avatarUrl} crossOrigin='anonymous' alt='User Avatar' />
                        </div>
                      ) : (
                        <div
                          className={
                            'symbol-label fs-3 fw-bold bg-' +
                            getStyleByIndex(2) +
                            ' text-inverse-' +
                            getStyleByIndex(2)
                          }
                        >
                          {getInitials(user?.firstName, user?.lastName)}
                        </div>
                      )}
                    </div>
                    <div className='d-flex flex-column'>
                      <div
                        className='fw-bolder d-flex align-items-center fs-5 lh22'
                        title={user?.roles?.join(',')}
                      >
                        {user?.firstName} {user?.lastName}
                      </div>
                      <div
                        className='fw-bold text-muted fs-7'
                        style={{
                          wordBreak: 'break-all',
                          maxWidth: '200px',
                        }}
                      >
                        {user?.email}
                      </div>
                    </div>
                  </div>
                </div>

                <div className='menu-item px-5'>
                  {isInterviewInProgress ? (
                    <span
                      className='menu-link px-5 disabled text-muted cursor-not-allowed'
                      title='Cannot view profile during an active interview'
                    >
                      My Profile
                    </span>
                  ) : (
                    <Link
                      className='menu-link px-5'
                      to={UrlParamReplace(getProfileLink())}
                      onClick={() => setIsProfileMenuOpen(false)}
                    >
                      <span className='menu-text'>My Profile</span>
                    </Link>
                  )}
                </div>

                <div className='separator my-2'></div>

                <div className='menu-item px-5'>
                  <Link
                    className='menu-link px-5'
                    to={'/logout'}
                    onClick={() => setIsProfileMenuOpen(false)}
                  >
                    <span className='menu-text'>Sign out</span>
                  </Link>
                </div>
              </div>
            </div>
          </nav>
        </div>
      </div>
      <AlertModal
        show={showDesktopOnlyAlert}
        title='Mobile not yet supported'
        onOK={() => {
          setShowDesktopOnlyAlert(false)
        }}
      >
        This platform does not currently support mobile or tablets. Please login using a desktop or
        laptop computer.
      </AlertModal>
      <AlertModal
        show={showOfflinePopup}
        title='Connection interrupted'
        okText='🗘 Reload now'
        onOK={() => {
          window.location.reload()
        }}
      >
        Please wait while we try connecting again. Please reload if this is taking long to connect
        again
      </AlertModal>
    </header>
  )
}

export {Header}
